// What the Stroke slice means. Its clot, collaterals and thrombectomy name a tissue-fate run (hemo's
// ixi165-stroke-<model>-<site>-<grade>-<treatment>[-distal]), and say which clots the live solve has at a
// given time: after a thrombectomy the vessel is open again, as the tissue runs assume (reperfusion
// restores baseline flow).

import type { AtlasState, Occlusion } from './store.ts';
import { scenarioById } from '../scenarios/index.ts';
import type { LiveClot } from '../data/sims/live.ts';
import type { GrowthTable } from '../data/sims/hemo.ts';
import { scoredOf, type IslesCase, type IslesComparison } from '../data/sims/isles.ts';
import { groupScores, v5Group, type GroupScores, type IslesV5 } from '../data/isles-v5.ts';
import ISLES_V5_DATA from '../data/isles-v5.json';

/** Clot sites hemo ran tissue fate for, by live solver vessel. hemo's ICA clot is cervical (in the
 *  openBF network's l-ica-cervical), below the PCom: the network's extracranial segment. Its 5 mm
 *  terminal segment runs on from the PCom to the bifurcation, and a clot there (carotid T) has no run. */
const SITES: [RegExp, string][] = [
  [/^ba:left-mca-m1$/, 'l-m1'],
  [/^ba:left-ica-extracranial$/, 'l-ica'],
];

/** The tissue runs' site for a clot in this vessel; null where hemo has no tissue run. */
export function siteOf(vessel: string | null | undefined): string | null {
  return (vessel && SITES.find(([re]) => re.test(vessel))?.[1]) || null;
}

/** IXI-165's left M1 in hemo's v4 and v5 networks: l-m1a runs 10.28 mm from the ICA terminus to where the
 *  lenticulostriate arteries leave it, and l-m1b 18.76 mm on from there (graph.json of their ixi165-1d runs). */
const M1A_MM = 10.28;
const M1B_MM = 18.76;

/** Where the lenticulostriate arteries leave the left M1, as a fraction of it from the terminus (35 %).
 *  hemo ran a clot on each side: proximal, mid-l-m1a, and distal, mid-l-m1b (its "-distal" runs). The
 *  live network has no branch there, so only the tissue run and its patients change across it. */
export const LSA_ORIGIN = M1A_MM / (M1A_MM + M1B_MM);

/** hemo's two M1 clots on that scale: 18 % and 68 %. */
export const M1_CLOTS = { proximal: (0.5 * M1A_MM) / (M1A_MM + M1B_MM), distal: (M1A_MM + 0.5 * M1B_MM) / (M1A_MM + M1B_MM) };

/** '-distal' for a left M1 clot beyond the lenticulostriate origin; '' before it, and for every other site. */
export function partOf(o: Occlusion | null | undefined): '' | '-distal' {
  return o && o.vessel === 'ba:left-mca-m1' && o.s >= LSA_ORIGIN ? '-distal' : '';
}

/** Collaterals on/off as the tissue runs' collateral grade. hemo also ran 'intermediate', which a switch can't
 *  reach; v5 takes it whatever the switch says (scenarios' HEMO_MODEL), and the switch still drives the live solve. */
export const gradeOf = (collaterals: boolean) => (collaterals ? 'good' : 'poor');

/** Thrombectomy hours as the tissue runs' treatment arm. */
export const treatmentOf = (hours: number | null) => (hours ? `reperf-${hours}h` : 'untreated');

type Slice = Pick<AtlasState, 'scenario' | 'stroke'>;

/** The run-template values the slice names ({site}, {part}, {grade}, {treatment}); empty for scenes
 *  without clinical controls. */
export function strokeChoices(s: Slice): Record<string, string> {
  if (!scenarioById(s.scenario).live?.clinical) return {};
  const o = s.stroke.occlusion;
  const site = siteOf(o?.vessel);
  return { ...(site ? { site, part: partOf(o) } : {}), grade: gradeOf(s.stroke.collaterals), treatment: treatmentOf(s.stroke.thrombectomyH) };
}

/** A stable key for a set of template values. */
export const choiceKey = (c: Record<string, string>) =>
  Object.keys(c)
    .sort()
    .map((k) => `${k}=${c[k]}`)
    .join('&');

/** When the clotted vessel opens again (thrombectomy), in scene seconds; null when it stays shut. */
export function reopensAt(s: Slice): number | null {
  const h = s.stroke.thrombectomyH;
  return scenarioById(s.scenario).live?.clinical && s.stroke.occlusion && h ? h * 3600 : null;
}

/** The clots the live solve has at the scene's time: the occlusion, until a thrombectomy reopens it. */
export function clotsNow(s: Slice & Pick<AtlasState, 'time'>): LiveClot[] {
  const o = s.stroke.occlusion;
  if (!o) return [];
  const t = reopensAt(s);
  return t !== null && s.time >= t ? [] : [{ vessel: o.vessel, s: o.s }];
}

/** The tissue states the panel lists (`growth.csv` columns), in its order; a run without one (no
 *  oligaemia band yet) leaves that row out rather than showing a zero. */
export const TISSUE_STATES: { col: string; label: string; token: string }[] = [
  { col: 'oligaemia_ml', label: 'Oligaemia', token: 'oligaemia' },
  { col: 'penumbra_ml', label: 'Penumbra', token: 'penumbra' },
  { col: 'core_ml', label: 'Core', token: 'core' },
  { col: 'reperfused_saved_ml', label: 'Reperfused', token: 'reperfused' },
];

/** Tissue volumes (mL) at time t, linear between the run's rows and held at its ends; only the columns it has. */
export function tissueAt(table: GrowthTable, t: number): { col: string; label: string; token: string; ml: number }[] {
  const x = table.time;
  if (!x.length) return [];
  let i = 0;
  while (i < x.length - 1 && x[i + 1]! <= t) i++;
  const j = Math.min(x.length - 1, i + 1);
  const w = j > i && t > x[i]! ? Math.min(1, (t - x[i]!) / (x[j]! - x[i]!)) : 0;
  return TISSUE_STATES.filter((st) => table.values[st.col]).map((st) => {
    const v = table.values[st.col]!;
    return { ...st, ml: v[i]! + (v[j]! - v[i]!) * w };
  });
}

/** The ISLES'24 patients hemo matched to the slice's tissue run: the same clot site and side of the
 *  lenticulostriate origin (its tables write l-m1, l-m1-distal, l-ica), collateral grade and
 *  reperfusion arm ('untreated' or '3h'); right-sided patients were compared with the mirrored run.
 *  None for a clot without a run. */
export function islesMatched(cases: IslesCase[], choices: Record<string, string>): IslesCase[] {
  const { site, part = '', grade, treatment } = choices;
  if (!site || !grade || !treatment) return [];
  const arm = treatment.replace(/^reperf-/, '');
  return cases.filter((c) => c.site === site + part && c.grade === grade && c.treatment === arm);
}

/** BLOOD-002's v5 scores, bundled from its frozen evidence (scripts/isles-v5.ts). */
export const ISLES_V5 = ISLES_V5_DATA as IslesV5;

/** What Compare with ISLES'24 sets side by side for the run on screen: the matched patients' medians,
 *  the model's infarct beside no infarct and the admission core. */
export interface IslesView {
  /** BLOOD-002's protocol id, or the comparison export's sims path. */
  from: string;
  /** The model the scores are for ('v5', 'v4'). */
  scored: string;
  /** BLOOD-002's arm for the run ('3h' or 'untreated'); none for the export. */
  arm?: string;
  scores: GroupScores;
}

/** BLOOD-002's scores where it scored the run on screen (v5's proximal left M1 clot, untreated or at
 *  3 h), else hemo's comparison export's, which scored `exportScored` (v4); null where neither has a
 *  patient for the run. */
export function islesView(choices: Record<string, string>, model: string | undefined, exported: IslesComparison | null, exportScored: string | undefined): IslesView | null {
  const g = v5Group(ISLES_V5, { ...choices, model });
  if (g) return { from: ISLES_V5.id, scored: ISLES_V5.scored, arm: g.arm, scores: groupScores(g.cases) };
  const matched = exported ? islesMatched(exported.cases, choices) : [];
  return exported && matched.length ? { from: exported.path, scored: exportScored ?? model ?? '', scores: groupScores(matched.map(scoredOf)) } : null;
}

/** The median of some numbers; null for none. */
export function median(xs: number[]): number | null {
  if (!xs.length) return null;
  const v = [...xs].sort((a, b) => a - b);
  const m = v.length >> 1;
  return v.length % 2 ? v[m]! : (v[m - 1]! + v[m]!) / 2;
}
